/**
 * @fileoverview Component for displaying a danger button.
 * This component includes a button with a red background and white text.
 */

import { ButtonHTMLAttributes, PropsWithChildren } from 'react'

/**
 * @function classNames
 * @description Combines multiple class names into a single string.
 * @param {...string[]} classes - The class names to combine.
 * @returns {string} - A single string of class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

const baseState =
    'inline-flex w-auto items-center justify-center rounded-md px-4 py-2.5 text-sm font-medium shadow-sm transition-colors'
const hoverState = 'hover:bg-red-800'
const focusState = 'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
const primaryState = 'bg-red-600 text-white'
const disabledState = 'bg-gray-200 text-gray-500 cursor-not-allowed'

/**
 * @function DangerButton
 * @description Component for displaying a danger button.
 * @param {PropsWithChildren<ButtonHTMLAttributes<HTMLButtonElement>>} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the danger button.
 */
export function DangerButton(props: PropsWithChildren<ButtonHTMLAttributes<HTMLButtonElement>>) {
    return (
        <button
            onClick={props.onClick}
            type="button"
            disabled={props.disabled}
            className={classNames(
                baseState,
                props.className,
                props.disabled ? disabledState : classNames(primaryState, hoverState, focusState)
            )}
        >
            {props.children}
        </button>
    )
}
